import { useState, useEffect, useCallback } from 'react';

export interface EditModeSettings {
  columns: {
    accountNumber: boolean;
    description: boolean;
    novalaBalance: boolean;
    bankBalance: boolean;
  };
  preferences: {
    autoUpdateSubNumbers: boolean;
    collapseOnLoad: boolean;
    confirmBeforeDelete: boolean;
  };
}

const DEFAULT_SETTINGS: EditModeSettings = {
  columns: {
    accountNumber: false,
    description: false,
    novalaBalance: true,
    bankBalance: false,
  },
  preferences: {
    autoUpdateSubNumbers: true,
    collapseOnLoad: false,
    confirmBeforeDelete: true,
  },
};

const STORAGE_KEY = 'novala.editModeSettings';

export function useEditModeSettings() {
  const [settings, setSettings] = useState<EditModeSettings>(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<EditModeSettings>;
        return {
          columns: { ...DEFAULT_SETTINGS.columns, ...(parsed.columns || {}) },
          preferences: { ...DEFAULT_SETTINGS.preferences, ...(parsed.preferences || {}) },
        };
      }
    } catch {}
    return DEFAULT_SETTINGS;
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {}
  }, [settings]);

  const toggleColumn = useCallback((key: keyof EditModeSettings['columns']) => {
    setSettings((prev) => ({
      ...prev,
      columns: { ...prev.columns, [key]: !prev.columns[key] },
    }));
  }, []);

  const togglePreference = useCallback((key: keyof EditModeSettings['preferences']) => {
    setSettings((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, [key]: !prev.preferences[key] },
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  return { settings, toggleColumn, togglePreference, resetToDefaults };
}